/**
 * Admin Bug Tracker
 * Lists all bug reports from the bug_reports table
 */

import { useState, useEffect } from 'react';
import { Bug, CheckCircle, Clock, AlertCircle, Loader2, RefreshCw } from 'lucide-react';

export default function AdminBugTracker() {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, open, resolved

  async function fetchBugs() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/bugs', { credentials: 'include' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to load bugs');
      }
      const data = await res.json();
      setBugs(data.bugs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchBugs(); }, []);

  async function resolveBug(id) {
    try {
      const res = await fetch(`/api/admin/bugs/${id}/resolve`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to resolve');
      fetchBugs();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  const filteredBugs = bugs.filter(b => {
    if (filter === 'all') return true;
    return b.status === filter;
  });

  const statusConfig = {
    open: { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Open' },
    resolved: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', label: 'Resolved' },
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <Bug className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Bug Tracker</h1>
            <p className="text-sm text-gray-500">{bugs.filter(b => b.status === 'open').length} open issues</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
          <button
            onClick={fetchBugs}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 font-medium">{error}</p>
          <button
            onClick={fetchBugs}
            className="mt-3 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50"
          >
            Try Again
          </button>
        </div>
      ) : filteredBugs.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
          <Bug className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No bugs found</p>
          <p className="text-sm text-gray-400 mt-1">Great job! No issues to report.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBugs.map((bug) => {
            const status = statusConfig[bug.status] || statusConfig.open;
            const StatusIcon = status.icon;
            return (
              <div
                key={bug.id}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg ${status.bg} flex items-center justify-center flex-shrink-0`}>
                    <StatusIcon className={`w-5 h-5 ${status.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{bug.title || 'Untitled Bug'}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                      {bug.priority && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          bug.priority === 'high' ? 'bg-red-100 text-red-700' :
                          bug.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {bug.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{bug.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Reported {new Date(bug.created_at).toLocaleDateString()}
                      </span>
                      {bug.page && <span>Page: {bug.page}</span>}
                      {bug.reporter_email && <span>By: {bug.reporter_email}</span>}
                    </div>
                  </div>
                  {bug.status === 'open' && (
                    <button
                      onClick={() => resolveBug(bug.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Resolve
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
